<template>
  <HeaderBar />
  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'viewCar', params: { id: car?.id } }">{{
      t('back')
    }}</ButtonBack>
  </ButtonBackContainer>
  <section class="carvia-container flex flex-col justify-center">
    <div class="flex justify-center space-y-3">
      <div class="grow">
        <div v-if="!isLoading && car && car.status !== CarStatus.ORDERED">
          <h2 class="mb-4">
            {{ t('carStatus') }}
          </h2>
          <EditCarStatusForm
            :is-saving="isUpdating || isLoadingMilageDeviation"
            :car="car"
            :auto-enter-milage="autoEnterMilage"
            :last-booking="lastBooking"
            @on-submit="handleSubmit"
          />
        </div>
      </div>
    </div>
    <div v-if="isLoading" class="flex justify-center"><Spinner /></div>
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import type { CarStatusFormValues } from '@/entities/cars/car.entity';
import { useI18n } from 'vue-i18n';
import { useCar, useUpdateCar } from '@/queries/use-cars';
import { useUiStore } from '@/stores/ui.store';
import EditCarStatusForm from './EditCarStatusForm.vue';
import { useRoute } from 'vue-router';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import { computed, watch, watchEffect } from 'vue';
import { useAllocations } from '@/queries/use-allocations';
import { Order } from '@/entities/pagination/order.enum';
import { CarStatus } from '@/entities/cars/car-status.enum';
import router from '@/router';
import { AllocationStatus, AllocationType, FindAllAllocationsSort } from '@carvia/ros-client-types';
import { useConfirmMilageDeviation } from '@/hooks/use-confirm-milage-deviation.ts';

const uiStore = useUiStore();
const { t } = useI18n();
const route = useRoute();
const { data: car, isLoading } = useCar(route.params.id as string, route.params.id !== 'new');
const autoEnterMilage = route.params.autoEnterMilage === 'auto-enter-milage';

const { data: lastAllocations } = useAllocations({
  limit: 1,
  order: Order.DESC,
  sort: [FindAllAllocationsSort.END_DATE],
  filter: [
    {
      carIds: [route.params.id as string],
      types: [AllocationType.BOOKING],
      status: [AllocationStatus.RETURNED],
    },
  ],
});
const { confirmPreparation, isLoading: isLoadingMilageDeviation } = useConfirmMilageDeviation();
const lastBooking = computed(() => {
  const booking = lastAllocations.value?.pages?.[0]?.items?.[0]?.booking;
  if (!booking) return undefined;
  return { id: booking.id, bookingNumber: booking.bookingNumber };
});

watch(
  car,
  () => {
    // Reroute to fleet in page, if car is not float in already
    if (car.value?.status === CarStatus.ORDERED) {
      router.replace({
        name: 'completeCarFleetIn',
        params: { id: car.value.id },
      });
    }
  },
  { immediate: true },
);

watchEffect(() => {
  uiStore.setHeaderTitle(t('carStatus'));
});

const { mutate: updateCar, isPending: isUpdating } = useUpdateCar();
const handleSubmit = async (values: CarStatusFormValues) => {
  if (!car.value) {
    return;
  }
  const { comment, isConfirmed, reason } = await confirmPreparation(car.value, values.milage);
  if (!isConfirmed) {
    return;
  }
  updateCar({
    id: route.params.id as string,
    car: { ...values, milageDeviationComment: comment, milageDeviationReason: reason },
  });
};
</script>

<i18n lang="json">
{
  "en": {
    "carStatus": "Car Status"
  },
  "de": {
    "carStatus": "Fahrzeugstatus"
  },
  "es": {
    "carStatus": "Estado del vehículo"
  }
}
</i18n>
